import React, { useState } from 'react';
import { 
  PenTool, 
  Calendar, 
  User, 
  Eye, 
  Heart, 
  MessageCircle,
  Share,
  Filter,
  Search,
  TrendingUp,
  Clock,
  Tag
} from 'lucide-react';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  authorImage: string;
  publishedAt: string;
  readTime: string;
  views: number;
  likes: number;
  comments: number;
  category: string;
  tags: string[];
  featured: boolean;
}

const Blogs: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { id: 'all', name: 'All Posts' },
    { id: 'monetary-policy', name: 'Monetary Policy' },
    { id: 'fiscal-policy', name: 'Fiscal Policy' },
    { id: 'market-analysis', name: 'Market Analysis' },
    { id: 'global-economics', name: 'Global Economics' },
    { id: 'economic-theory', name: 'Economic Theory' },
  ];

  const mockBlogPosts: BlogPost[] = [
    {
      id: '1',
      title: 'The Fed\'s Dilemma: Balancing Inflation Control and Economic Growth',
      excerpt: 'As the Federal Reserve continues to navigate the complex economic landscape, policymakers face the challenging task of controlling inflation while maintaining economic growth. This analysis explores the trade-offs and potential outcomes.',
      content: '',
      author: 'Dr. Sarah Mitchell',
      authorImage: '/api/placeholder/40/40',
      publishedAt: '2024-01-15',
      readTime: '8 min read',
      views: 2450,
      likes: 89,
      comments: 23,
      category: 'monetary-policy',
      tags: ['Federal Reserve', 'Inflation', 'Interest Rates'],
      featured: true
    },
    {
      id: '2',
      title: 'India\'s Economic Resilience: Lessons from Recent Global Challenges',
      excerpt: 'Despite facing multiple global headwinds, India\'s economy has shown remarkable resilience. We examine the key factors contributing to this stability and what it means for future growth prospects.',
      content: '',
      author: 'Prof. Rajesh Kumar',
      authorImage: '/api/placeholder/40/40',
      publishedAt: '2024-01-14',
      readTime: '12 min read',
      views: 1890,
      likes: 67,
      comments: 31,
      category: 'global-economics',
      tags: ['India', 'Economic Growth', 'Resilience'],
      featured: true
    },
    {
      id: '3',
      title: 'The Rise of Digital Currencies: Central Bank Digital Currencies (CBDCs)',
      excerpt: 'Central banks worldwide are exploring digital currencies as the future of money. This comprehensive analysis examines the implications, benefits, and challenges of CBDCs for the global financial system.',
      content: '',
      author: 'Dr. Emily Chen',
      authorImage: '/api/placeholder/40/40',
      publishedAt: '2024-01-13',
      readTime: '15 min read',
      views: 3200,
      likes: 145,
      comments: 56,
      category: 'monetary-policy',
      tags: ['CBDC', 'Digital Currency', 'Central Banks'],
      featured: false
    },
    {
      id: '4',
      title: 'Market Volatility in 2024: What Investors Need to Know',
      excerpt: 'With geopolitical tensions and economic uncertainties, market volatility has become a key concern for investors. Our analysis provides insights into navigating these turbulent times.',
      content: '',
      author: 'Michael Thompson',
      authorImage: '/api/placeholder/40/40',
      publishedAt: '2024-01-12',
      readTime: '10 min read',
      views: 1650,
      likes: 78,
      comments: 19,
      category: 'market-analysis',
      tags: ['Market Volatility', 'Investment', 'Risk Management'],
      featured: false
    },
    {
      id: '5',
      title: 'Fiscal Policy in the Post-Pandemic Era: New Paradigms',
      excerpt: 'The COVID-19 pandemic has fundamentally changed how governments approach fiscal policy. We explore the new paradigms emerging in public finance and their long-term implications.',
      content: '',
      author: 'Dr. Lisa Rodriguez',
      authorImage: '/api/placeholder/40/40',
      publishedAt: '2024-01-11',
      readTime: '11 min read',
      views: 2100,
      likes: 92,
      comments: 27,
      category: 'fiscal-policy',
      tags: ['Fiscal Policy', 'Government Spending', 'Public Finance'],
      featured: false
    },
    {
      id: '6',
      title: 'The Economics of Climate Change: Policy Responses and Market Solutions',
      excerpt: 'Climate change presents unprecedented economic challenges. This analysis examines policy responses and market-based solutions to address environmental concerns while maintaining economic growth.',
      content: '',
      author: 'Prof. David Green',
      authorImage: '/api/placeholder/40/40',
      publishedAt: '2024-01-10',
      readTime: '14 min read',
      views: 2800,
      likes: 134,
      comments: 42,
      category: 'economic-theory',
      tags: ['Climate Change', 'Environmental Economics', 'Policy'],
      featured: false
    }
  ];

  const filteredPosts = mockBlogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const featuredPosts = filteredPosts.filter(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Economics Blog</h1>
        <p className="mt-2 text-gray-600">
          Expert insights, analysis, and commentary on economic trends and policy
        </p>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search blog posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Featured Articles</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {featuredPosts.map(post => (
              <div key={post.id} className="card hover:shadow-lg transition-shadow cursor-pointer">
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    Featured
                  </span>
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="h-4 w-4 mr-1" />
                    {formatDate(post.publishedAt)}
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">{post.title}</h3>
                <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{post.author}</p>
                      <p className="text-xs text-gray-500">{post.readTime}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Eye className="h-4 w-4 mr-1" />
                      {post.views.toLocaleString()}
                    </div>
                    <div className="flex items-center">
                      <Heart className="h-4 w-4 mr-1" />
                      {post.likes}
                    </div>
                    <div className="flex items-center">
                      <MessageCircle className="h-4 w-4 mr-1" />
                      {post.comments}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Posts */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Latest Articles ({regularPosts.length})
        </h2>
        <div className="space-y-6">
          {regularPosts.map(post => (
            <div key={post.id} className="card hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-6">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {categories.find(c => c.id === post.category)?.name}
                      </span>
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(post.publishedAt)}
                      </div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600">
                      <Share className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{post.title}</h3>
                  <p className="text-gray-600 mb-4">{post.excerpt}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{post.author}</p>
                        <div className="flex items-center text-xs text-gray-500">
                          <Clock className="h-3 w-3 mr-1" />
                          {post.readTime}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Eye className="h-4 w-4 mr-1" />
                        {post.views.toLocaleString()}
                      </div>
                      <div className="flex items-center">
                        <Heart className="h-4 w-4 mr-1" />
                        {post.likes}
                      </div>
                      <div className="flex items-center">
                        <MessageCircle className="h-4 w-4 mr-1" />
                        {post.comments}
                      </div>
                    </div>
                  </div>
                  
                  {/* Tags */}
                  <div className="flex items-center space-x-2 mt-4">
                    <Tag className="h-4 w-4 text-gray-400" />
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map(tag => (
                        <span key={tag} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Newsletter Signup */}
      <div className="card bg-gradient-to-r from-primary-50 to-blue-50 border-primary-200">
        <div className="text-center">
          <PenTool className="mx-auto h-12 w-12 text-primary-600 mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Stay Updated</h3>
          <p className="text-gray-600 mb-6">
            Subscribe to our newsletter for the latest economic insights and analysis delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center space-y-3 sm:space-y-0 sm:space-x-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button className="btn-primary whitespace-nowrap">
              Subscribe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blogs; 